import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { fmtDate, fmtNum, LOCATION_LABELS } from "../lib/format";
import { canOperationalApprove, hasModuleAccess, useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import AssetIncidents from "./AssetIncidents";
import RecoveryDialog from "../components/RecoveryDialog";
import { useSearchParams } from "react-router-dom";

export default function MissingDamaged() {
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState("missing");
  const canSeeAssets = hasModuleAccess(user, "asset_management", "view");
  const [outerTab, setOuterTab] = useState(
    canSeeAssets && sp.get("tab") === "assets" ? "assets" : "linen",
  );
  const [missing, setMissing] = useState([]);
  const [damage, setDamage] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({
    item_id: "",
    qty: 1,
    source_location: "ROOM_CLEAN",
    reason: "",
    room_id: "",
    vendor_id: "",
  });
  const [dForm, setDForm] = useState({
    item_id: "",
    qty: 1,
    source_location: "ROOM_CLEAN",
    reason: "",
    room_id: "",
    vendor_id: "",
  });
  const [recoverMiss, setRecoverMiss] = useState(null); // missing report being recovered
  const [recoverDmg, setRecoverDmg] = useState(null); // damage report being recovered
  const [approveTarget, setApproveTarget] = useState(null); // missing report pending loss approval
  const [approveReason, setApproveReason] = useState("");
  const [approveBusy, setApproveBusy] = useState(false);

  const load = async () => {
    const [m, d, i, r, v] = await Promise.all([
      api.get("/missing-reports"),
      api.get("/damage-reports"),
      api.get("/items"),
      api.get("/rooms"),
      api.get("/vendors"),
    ]);
    setMissing(m.data);
    setDamage(d.data);
    setItems(i.data);
    setRooms(r.data);
    setVendors(v.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submitMissing = async () => {
    try {
      await api.post("/missing-reports", {
        ...form,
        qty: Number(form.qty),
        room_id: form.room_id || null,
        vendor_id: form.vendor_id || null,
      });
      toast.success("Missing report created");
      setForm({
        item_id: "",
        qty: 1,
        source_location: "ROOM_CLEAN",
        reason: "",
        room_id: "",
        vendor_id: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitDamage = async () => {
    try {
      await api.post("/damage-reports", {
        ...dForm,
        qty: Number(dForm.qty),
        room_id: dForm.room_id || null,
        vendor_id: dForm.vendor_id || null,
      });
      toast.success("Damage recorded (moved to quarantine)");
      setDForm({
        item_id: "",
        qty: 1,
        source_location: "ROOM_CLEAN",
        reason: "",
        room_id: "",
        vendor_id: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approveLoss = (r) => {
    setApproveTarget(r);
    setApproveReason("");
  };
  const submitApproveLoss = async () => {
    if (!approveReason.trim()) {
      toast.error("Please state the reason for approving this loss");
      return;
    }
    setApproveBusy(true);
    try {
      await api.post("/missing-reports/approve-loss", {
        report_id: approveTarget.id,
        remarks: approveReason.trim(),
      });
      toast.success("Loss approved");
      setApproveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setApproveBusy(false);
    }
  };

  const recover = (r) => setRecoverMiss(r);
  const submitRecoverMiss = async (payload) => {
    await api.post("/missing-reports/recover", {
      report_id: recoverMiss.id,
      ...payload,
    });
    toast.success(
      payload.recovered_qty < recoverMiss.qty
        ? "Partially recovered"
        : "Recovered",
    );
    load();
  };
  const submitRecoverDmg = async (payload) => {
    await api.post("/damage-reports/recover", {
      report_id: recoverDmg.id,
      ...payload,
    });
    toast.success(
      payload.recovered_qty < recoverDmg.qty
        ? "Partially recovered to stock"
        : "Recovered to stock",
    );
    load();
  };

  const dispose = async (item_id, qty) => {
    const reason = window.prompt("Reason for disposal?");
    if (!reason) return;
    try {
      await api.post("/damage-reports/dispose", {
        item_id,
        qty: Number(qty),
        reason,
      });
      toast.success("Disposed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const itemName = (id) => items.find((x) => x.id === id)?.name || id;
  const roomNum = (id) => rooms.find((x) => x.id === id)?.number || "—";
  const [mFilter, setMFilter] = useState("all");
  const [dFilter, setDFilter] = useState("all");
  const isOpenMiss = (r) => (r.status || "open") === "open";
  const isOpenDmg = (r) => (r.status || "quarantined") === "quarantined";
  const applyFilter = (list, f, openFn) =>
    f === "all"
      ? list
      : f === "open"
        ? list.filter(openFn)
        : list.filter((r) => !openFn(r));
  const REACT_URL = process.env.REACT_APP_BACKEND_URL;
  const RecoveryInfo = ({ r }) =>
    r.recovery_reason || r.recovered_qty != null ? (
      <div
        className="text-[11px] text-slate-500 mt-1 space-y-0.5"
        data-testid={`recovery-info-${r.id}`}
      >
        {r.recovered_qty != null && (
          <div>
            Recovered <b>{r.recovered_qty}</b>
            {r.remaining_qty ? ` · ${r.remaining_qty} left` : ""}
            {r.recovery_destination
              ? ` → ${LOCATION_LABELS[r.recovery_destination] || r.recovery_destination}`
              : ""}
          </div>
        )}
        {r.recovery_reason && <div>Reason: {r.recovery_reason}</div>}
        {r.location_found && <div>Found: {r.location_found}</div>}
        {r.recovery_proof_url && (
          <a
            href={`${REACT_URL}${r.recovery_proof_url}`}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={`${REACT_URL}${r.recovery_proof_url}`}
              alt="proof"
              className="w-10 h-10 object-cover rounded border mt-1"
            />
          </a>
        )}
      </div>
    ) : null;

  return (
    <Layout>
      <h1 className="section-title" data-testid="page-title">
        {t("nav.missing_damaged")}
      </h1>
      <div className="section-sub mb-4">
        {outerTab === "assets"
          ? "Asset & inventory incidents. Damaged items stay accountable until Admin approves loss/disposal."
          : t("sub.missing_damaged")}
      </div>

      <Tabs value={outerTab} onValueChange={setOuterTab} className="mb-4">
        <TabsList data-testid="md-outer-tabs">
          <TabsTrigger value="linen" data-testid="md-tab-linen">
            Linen
          </TabsTrigger>
          {canSeeAssets && (
            <TabsTrigger value="assets" data-testid="md-tab-assets">
              Assets &amp; Inventory
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="linen">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-3">
              <TabsTrigger value="missing" data-testid="tab-missing">
                Missing
              </TabsTrigger>
              <TabsTrigger value="damage" data-testid="tab-damage">
                Damaged
              </TabsTrigger>
            </TabsList>

            <TabsContent value="missing">
              <Card className="p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                  <div className="md:col-span-2">
                    <Label>Item</Label>
                    <Select
                      value={form.item_id}
                      onValueChange={(v) => setForm({ ...form, item_id: v })}
                    >
                      <SelectTrigger className="h-11" data-testid="miss-item">
                        <SelectValue placeholder="Item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((it) => (
                          <SelectItem key={it.id} value={it.id}>
                            {it.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.qty}
                      onChange={(e) =>
                        setForm({ ...form, qty: e.target.value })
                      }
                      className="h-11"
                      data-testid="miss-qty"
                    />
                  </div>
                  <div>
                    <Label>Source Location</Label>
                    <Select
                      value={form.source_location}
                      onValueChange={(v) =>
                        setForm({ ...form, source_location: v })
                      }
                    >
                      <SelectTrigger className="h-11" data-testid="miss-loc">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "STORE_CLEAN",
                          "ROOM_CLEAN",
                          "DIRTY_HOLDING",
                          "EXTERNAL_LAUNDRY",
                        ].map((l) => (
                          <SelectItem key={l} value={l}>
                            {LOCATION_LABELS[l]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.source_location === "ROOM_CLEAN" && (
                    <div>
                      <Label>Room</Label>
                      <Select
                        value={form.room_id}
                        onValueChange={(v) => setForm({ ...form, room_id: v })}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Room" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <Label>Reason</Label>
                    <Input
                      value={form.reason}
                      onChange={(e) =>
                        setForm({ ...form, reason: e.target.value })
                      }
                      className="h-11"
                      data-testid="miss-reason"
                    />
                  </div>
                  <div>
                    <Button
                      className="h-11 bg-slate-900 text-white hover:bg-slate-800"
                      onClick={submitMissing}
                      data-testid="miss-submit"
                    >
                      Report Missing
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="p-0 overflow-hidden">
                <div className="flex items-center gap-2 p-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Show</span>
                  <Select value={mFilter} onValueChange={setMFilter}>
                    <SelectTrigger
                      className="h-8 w-32"
                      data-testid="missing-filter"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="missing-table">
                    <thead>
                      <tr>
                        <th>Reported</th>
                        <th>Item</th>
                        <th className="num">Qty</th>
                        <th>Source</th>
                        <th>Reason</th>
                        <th>Status / Resolution</th>
                        <th>By</th>
                        <th className="no-print">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyFilter(missing, mFilter, isOpenMiss).map((r) => (
                        <tr key={r.id}>
                          <td>{fmtDate(r.submitted_at)}</td>
                          <td>{itemName(r.item_id)}</td>
                          <td className="num">{fmtNum(r.qty)}</td>
                          <td>
                            {LOCATION_LABELS[r.source_location] ||
                              r.source_location}
                            {r.room_id ? ` · Room ${roomNum(r.room_id)}` : ""}
                          </td>
                          <td>{r.reason}</td>
                          <td>
                            <Badge
                              className={
                                r.status === "open"
                                  ? "bg-amber-100 border text-amber-800"
                                  : r.status === "recovered"
                                    ? "bg-emerald-100 border text-emerald-800"
                                    : r.status === "partially_recovered"
                                      ? "bg-teal-100 border text-teal-800"
                                      : "bg-red-100 border text-red-800"
                              }
                            >
                              {String(r.status).replace("_", " ")}
                            </Badge>
                            <RecoveryInfo r={r} />
                          </td>
                          <td>{r.submitted_by_name}</td>
                          <td className="no-print">
                            {r.status === "open" &&
                              canOperationalApprove(user) && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => approveLoss(r)}
                                  data-testid={`approve-loss-${r.id}`}
                                >
                                  Approve Loss
                                </Button>
                              )}
                            {r.status === "open" &&
                              canOperationalApprove(user) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => recover(r)}
                                  data-testid={`recover-${r.id}`}
                                >
                                  Recovered
                                </Button>
                              )}
                          </td>
                        </tr>
                      ))}
                      {missing.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center text-slate-500 py-5"
                          >
                            No missing reports.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="damage">
              <Card className="p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                  <div className="md:col-span-2">
                    <Label>Item</Label>
                    <Select
                      value={dForm.item_id}
                      onValueChange={(v) => setDForm({ ...dForm, item_id: v })}
                    >
                      <SelectTrigger className="h-11" data-testid="dmg-item">
                        <SelectValue placeholder="Item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((it) => (
                          <SelectItem key={it.id} value={it.id}>
                            {it.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={dForm.qty}
                      onChange={(e) =>
                        setDForm({ ...dForm, qty: e.target.value })
                      }
                      className="h-11"
                      data-testid="dmg-qty"
                    />
                  </div>
                  <div>
                    <Label>Source</Label>
                    <Select
                      value={dForm.source_location}
                      onValueChange={(v) =>
                        setDForm({ ...dForm, source_location: v })
                      }
                    >
                      <SelectTrigger className="h-11" data-testid="dmg-loc">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "STORE_CLEAN",
                          "ROOM_CLEAN",
                          "DIRTY_HOLDING",
                          "DIRTY_CHECKOUT",
                          "EXTERNAL_LAUNDRY",
                        ].map((l) => (
                          <SelectItem key={l} value={l}>
                            {LOCATION_LABELS[l]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {dForm.source_location === "ROOM_CLEAN" && (
                    <div>
                      <Label>Room</Label>
                      <Select
                        value={dForm.room_id}
                        onValueChange={(v) =>
                          setDForm({ ...dForm, room_id: v })
                        }
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Room" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <Label>Reason</Label>
                    <Input
                      value={dForm.reason}
                      onChange={(e) =>
                        setDForm({ ...dForm, reason: e.target.value })
                      }
                      className="h-11"
                      data-testid="dmg-reason"
                    />
                  </div>
                  <div>
                    <Button
                      className="h-11 bg-slate-900 text-white hover:bg-slate-800"
                      onClick={submitDamage}
                      data-testid="dmg-submit"
                    >
                      Report Damage
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="p-0 overflow-hidden">
                <div className="flex items-center gap-2 p-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Show</span>
                  <Select value={dFilter} onValueChange={setDFilter}>
                    <SelectTrigger
                      className="h-8 w-32"
                      data-testid="damage-filter"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="damage-table">
                    <thead>
                      <tr>
                        <th>Reported</th>
                        <th>Item</th>
                        <th className="num">Qty</th>
                        <th>Source</th>
                        <th>Reason</th>
                        <th>Status / Resolution</th>
                        <th>By</th>
                        <th className="no-print">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyFilter(damage, dFilter, isOpenDmg).map((r) => (
                        <tr key={r.id}>
                          <td>{fmtDate(r.submitted_at)}</td>
                          <td>{itemName(r.item_id)}</td>
                          <td className="num">{fmtNum(r.qty)}</td>
                          <td>
                            {LOCATION_LABELS[r.source_location] ||
                              r.source_location}
                          </td>
                          <td>{r.reason}</td>
                          <td>
                            <Badge
                              className={
                                isOpenDmg(r)
                                  ? "bg-amber-100 border text-amber-800"
                                  : r.status === "partially_recovered"
                                    ? "bg-teal-100 border text-teal-800"
                                    : "bg-emerald-100 border text-emerald-800"
                              }
                            >
                              {isOpenDmg(r)
                                ? "quarantined"
                                : String(r.status).replace("_", " ")}
                            </Badge>
                            <RecoveryInfo r={r} />
                          </td>
                          <td>{r.submitted_by_name}</td>
                          <td className="no-print flex flex-wrap gap-2">
                            {r.status === "quarantined" || !r.status ? (
                              <>
                                {canOperationalApprove(user) && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => dispose(r.item_id, r.qty)}
                                    data-testid={`dispose-${r.id}`}
                                  >
                                    Approve Disposal
                                  </Button>
                                )}
                                {canOperationalApprove(user) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setRecoverDmg(r)}
                                    data-testid={`recover-dmg-${r.id}`}
                                  >
                                    Recover
                                  </Button>
                                )}
                              </>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                      {damage.length === 0 && (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center text-slate-500 py-5"
                          >
                            No damage reports.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>
        {canSeeAssets && (
          <TabsContent value="assets">
            <AssetIncidents embedded />
          </TabsContent>
        )}
      </Tabs>

      <RecoveryDialog
        open={!!recoverMiss}
        onOpenChange={(o) => !o && setRecoverMiss(null)}
        title={
          recoverMiss
            ? `Recover missing · ${itemName(recoverMiss.item_id)}`
            : ""
        }
        maxQty={recoverMiss?.qty || 1}
        destinationOptions={[
          { value: "STORE_CLEAN", label: "Store Clean" },
          { value: "ROOM_CLEAN", label: "Back to Room" },
        ]}
        rooms={rooms}
        onSubmit={submitRecoverMiss}
      />
      <RecoveryDialog
        open={!!recoverDmg}
        onOpenChange={(o) => !o && setRecoverDmg(null)}
        title={
          recoverDmg ? `Recover damaged · ${itemName(recoverDmg.item_id)}` : ""
        }
        maxQty={recoverDmg?.qty || 1}
        destinationOptions={[
          { value: "STORE_CLEAN", label: "Store Clean" },
          { value: "ROOM_CLEAN", label: "A Room" },
        ]}
        roomOption="ROOM_CLEAN"
        rooms={rooms}
        onSubmit={submitRecoverDmg}
      />

      <Dialog
        open={!!approveTarget}
        onOpenChange={(o) => !o && setApproveTarget(null)}
      >
        <DialogContent data-testid="approve-loss-dialog">
          <DialogHeader>
            <DialogTitle>Approve Loss</DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-3">
              <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Item</span>
                  <span className="font-semibold">
                    {itemName(approveTarget.item_id)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Quantity</span>
                  <span className="font-semibold">
                    {fmtNum(approveTarget.qty)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Source</span>
                  <span>
                    {LOCATION_LABELS[approveTarget.source_location] ||
                      approveTarget.source_location}
                    {approveTarget.room_id
                      ? ` · Room ${roomNum(approveTarget.room_id)}`
                      : ""}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Reported by</span>
                  <span>{approveTarget.submitted_by_name || "—"}</span>
                </div>
                <div className="pt-1 border-t mt-1">
                  <div className="text-slate-500">
                    Reason reported by housekeeping
                  </div>
                  <div
                    className="font-medium text-slate-800"
                    data-testid="approve-loss-hk-reason"
                  >
                    {approveTarget.reason || (
                      <span className="italic text-amber-600">
                        No reason was provided
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
                This permanently deducts {fmtNum(approveTarget.qty)} unit(s)
                from stock. State the reason for approving before continuing.
              </div>
              <div>
                <Label>
                  Approval reason <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={approveReason}
                  onChange={(e) => setApproveReason(e.target.value)}
                  placeholder="e.g. Confirmed lost during guest checkout; not recoverable"
                  className="mt-1"
                  data-testid="approve-loss-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApproveTarget(null)}
              disabled={approveBusy}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitApproveLoss}
              disabled={approveBusy || !approveReason.trim()}
              data-testid="approve-loss-confirm"
            >
              Approve Loss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
